<script setup lang="ts">
import WrButton from "~/components/global/WrButton.vue";
import WrIcon from "../global/WrIcon.vue";

const colorMode = useColorMode();
const isLight = computed(() => colorMode.value !== "dark");

interface UseCaseItem {
  title: string;
  description: string;
  svgIcon?: string;
}

const emit = defineEmits<{ "cta-clicked": [] }>();

withDefaults(
  defineProps<{
    heading: string;
    subcopy: string;
    items: UseCaseItem[];
    eyebrow?: string;
    ctaText?: string;
    ctaLink?: string | null;
  }>(),
  {
    eyebrow: "Industries",
    ctaText: "See industry use cases",
    ctaLink: null,
  },
);
</script>

<template>
  <section class="relative">
    <div class="text-center">
      <span class="fl-eyebrow"><span class="fl-eyebrow-dot" />{{ eyebrow }}</span>
      <h2 class="fl-heading">{{ heading }}</h2>
      <p class="fl-subcopy" :class="isLight ? 'text-black opacity-70' : 'text-white opacity-70'">{{ subcopy }}</p>
    </div>

    <div class="fl-rows mt-10 lg:mt-14 px-4 max-w-[820px] mx-auto">
      <div v-for="item in items" :key="item.title" class="fl-row group">
          <span class="fl-row-accent" />
          <div class="fl-icon">
            <WrIcon :name="item.svgIcon || 'streamline:fork-knife'" class="size-5 lg:size-6 text-[#12DBFF]" />
          </div>
          <div class="fl-row-body">
            <h3 class="fl-row-title">{{ item.title }}</h3>
            <p class="fl-row-desc">{{ item.description }}</p>
          </div>
        </div>
    </div>

    <div class="flex justify-center mt-10 lg:mt-12">
      <WrButton
        :link="ctaLink || undefined"
        @click="!ctaLink ? emit('cta-clicked') : undefined"
        container-class="h-[44px] lg:h-[48px]"
        append-class="text-sm lg:font-normal px-5 lg:px-6"
      >
        {{ ctaText }}
      </WrButton>
    </div>
  </section>
</template>

<style scoped>
.fl-eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.2em;
  color: #887fff; margin-bottom: 14px;
}
.fl-eyebrow-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: #12dbff; box-shadow: 0 0 8px rgba(18, 219, 255, 0.8);
  animation: fl-blink 2.4s ease-in-out infinite;
}
@keyframes fl-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
@media (min-width: 1024px) { .fl-eyebrow { font-size: 12px; } }

.fl-heading {
  font-family: "ITC Avant Garde Gothic Demi", "ITC Avant Garde Gothic", "AvantGarde", sans-serif;
  font-size: clamp(28px, 4vw, 48px); font-weight: 600; line-height: 1.1; text-align: center;
  background: linear-gradient(92deg, #12dbff -3.57%, #ba55fe 80.72%);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; color: transparent;
}
.fl-subcopy {
  font-family: "ITC Avant Garde Gothic", "AvantGarde", sans-serif;
  font-size: 16px; line-height: 1.6; letter-spacing: 0.36px; margin-top: 16px;
  text-align: center; max-width: 720px; margin-left: auto; margin-right: auto; padding: 0 16px;
}
@media (min-width: 1024px) { .fl-subcopy { font-size: 18px; } }

/* ── Industry rows ── */
.fl-rows { display: flex; flex-direction: column; gap: 12px; }
@media (min-width: 1024px) { .fl-rows { gap: 14px; } }

.fl-row {
  position: relative; overflow: hidden;
  display: flex; align-items: center; gap: 16px;
  border-radius: 14px; border: 1px solid rgba(136, 127, 255, 0.15);
  background: rgba(18, 0, 44, 0.35); padding: 16px 20px;
  transition: transform 0.3s ease, border-color 0.3s ease, background 0.3s ease, box-shadow 0.3s ease;
}
@media (min-width: 1024px) { .fl-row { gap: 18px; padding: 18px 24px; } }
.fl-row:hover {
  transform: translateX(4px);
  border-color: rgba(18, 219, 255, 0.4);
  background: rgba(18, 0, 44, 0.55);
  box-shadow: 0 16px 32px -24px rgba(18, 219, 255, 0.5);
}

.fl-row-accent {
  position: absolute; left: 0; top: 0; bottom: 0; width: 3px;
  background: linear-gradient(180deg, #12dbff, #ba55fe);
  transform: scaleY(0); transform-origin: top; transition: transform 0.4s ease;
}
.fl-row:hover .fl-row-accent { transform: scaleY(1); }

.fl-icon {
  width: 42px; height: 42px; flex-shrink: 0; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  border: 1px solid rgba(18, 219, 255, 0.25); background: rgba(18, 219, 255, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}
@media (min-width: 1024px) { .fl-icon { width: 46px; height: 46px; } }
.fl-row:hover .fl-icon {
  transform: scale(1.08) rotate(-4deg);
  box-shadow: 0 0 18px rgba(18, 219, 255, 0.3);
}

.fl-row-body { flex: 1; min-width: 0; }
.fl-row-title { font-size: 16px; font-weight: 600; color: #fff; }
@media (min-width: 1024px) { .fl-row-title { font-size: 17px; } }
.fl-row-desc { font-size: 13px; line-height: 1.55; color: #bcb2c3; margin-top: 3px; }
@media (min-width: 1024px) { .fl-row-desc { font-size: 14px; } }

.fl-row-arrow {
  flex-shrink: 0; color: #12dbff;
  opacity: 0; transform: translateX(-8px);
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.fl-row:hover .fl-row-arrow { opacity: 1; transform: translateX(0); }

/* Light theme */
html:not(.my-app-dark) .fl-eyebrow { color: #613faa; }
html:not(.my-app-dark) .fl-row {
  background: #fff;
  border-color: rgba(208, 208, 208, 0.5);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
}
html:not(.my-app-dark) .fl-row:hover {
  background: #fff;
  border-color: rgba(18, 219, 255, 0.5);
  box-shadow: 0 8px 20px rgba(18, 219, 255, 0.18);
}
html:not(.my-app-dark) .fl-icon {
  background: linear-gradient(135deg, rgba(18, 219, 255, 0.15) 0%, rgba(186, 85, 254, 0.2) 100%);
  border-color: transparent;
}
html:not(.my-app-dark) .fl-icon :deep(svg) { color: #501d71; }
html:not(.my-app-dark) .fl-row-title { color: #613faa; }
html:not(.my-app-dark) .fl-row-desc { color: #444; }
</style>
